import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import Logo from '@/components/Logo';
import {
  Search,
  MapPin,
  Star,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Filter,
  User,
  Stethoscope,
  Calendar,
  CheckCircle,
  X
} from 'lucide-react';

const ProfessionalsDirectory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [professionals, setProfessionals] = useState([]);
  const [filters, setFilters] = useState({ cities: [], specialties: [] });
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [selectedSpecialty, setSelectedSpecialty] = useState(searchParams.get('specialty') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [showFilters, setShowFilters] = useState(false);
  
  // City autocomplete state
  const [cityInput, setCityInput] = useState(searchParams.get('city') || '');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const cityInputRef = useRef(null);
  const citySuggestionsRef = useRef(null);

  // Fetch city suggestions from Nominatim
  const fetchCitySuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCitySuggestions([]);
      return;
    }
    
    setLoadingCities(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)},cameroon&countrycodes=cm&limit=10&addressdetails=1`,
        { 
          headers: { 
            'Accept-Language': 'fr',
            'User-Agent': 'DOCKHTA-Health-Platform/1.0'
          } 
        }
      );
      const data = await response.json();
      
      // Extract unique city names from results that are cities/towns/villages
      const validTypes = ['city', 'town', 'village', 'municipality'];
      const cities = new Set();
      
      data.forEach(item => {
        // Check addresstype or type
        if (validTypes.includes(item.addresstype) || validTypes.includes(item.type)) {
          const cityName = item.address?.city || item.address?.town || item.address?.village || item.name;
          if (cityName) cities.add(cityName);
        } else if (item.address) {
          // Fallback: extract city from address
          const cityName = item.address.city || item.address.town || item.address.village;
          if (cityName) cities.add(cityName);
        }
      });
      
      setCitySuggestions(Array.from(cities).slice(0, 8));
    } catch (error) {
      console.error('Error fetching city suggestions:', error);
    } finally {
      setLoadingCities(false);
    }
  }, []);
  
  // Debounce city input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (cityInput && cityInput !== selectedCity) {
        fetchCitySuggestions(cityInput);
        setShowCitySuggestions(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [cityInput, selectedCity, fetchCitySuggestions]);
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        citySuggestionsRef.current && 
        !citySuggestionsRef.current.contains(event.target) &&
        cityInputRef.current &&
        !cityInputRef.current.contains(event.target)
      ) {
        setShowCitySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProfessionals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (selectedCity) params.append('city', selectedCity);
      if (selectedSpecialty) params.append('specialty', selectedSpecialty);
      params.append('page', currentPage);
      params.append('limit', 20);
      
      const response = await api.get(`/products/professionals/directory?${params.toString()}`);
      setProfessionals(response.data.professionals || []);
      setFilters(response.data.filters || { cities: [], specialties: [] });
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
    } catch (err) {
      console.error('Error loading professionals:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCity, selectedSpecialty, currentPage]);

  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCity) params.set('city', selectedCity);
    if (selectedSpecialty) params.set('specialty', selectedSpecialty);
    if (currentPage > 1) params.set('page', currentPage);
    setSearchParams(params);
  }, [searchQuery, selectedCity, selectedSpecialty, currentPage, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadProfessionals();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCity('');
    setCityInput('');
    setSelectedSpecialty('');
    setCurrentPage(1);
  };
  
  const selectCity = (city) => {
    setSelectedCity(city);
    setCityInput(city);
    setShowCitySuggestions(false);
    setCurrentPage(1);
  };
  
  const clearCity = () => {
    setSelectedCity('');
    setCityInput('');
    setCurrentPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Annuaire des Professionnels de Santé | DOCKHTA</title>
        <meta name="description" content="Trouvez un médecin, spécialiste ou professionnel de santé au Cameroun. Consultez les avis, disponibilités et prenez rendez-vous en ligne." />
        <link rel="canonical" href="https://dockhta.com/professionnels" />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" data-testid="header-logo">
              <Logo size="sm" showSlogan={false} />
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/etablissements">
                <Button variant="ghost" size="sm" className="text-slate-600 hidden sm:flex">
                  Établissements
                </Button>
              </Link>
              <Link to="/login">
                <Button size="sm" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-4">
                  Connexion
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#1976D2] via-[#1565C0] to-[#0D47A1] text-white">
          <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
            <h1 className="text-2xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Trouvez votre professionnel de santé
            </h1>
            <p className="text-blue-100 text-sm sm:text-base max-w-2xl">
              Médecins, spécialistes, dentistes — {total} professionnels qualifiés au Cameroun
            </p>
          </div>
        </div>

        <main className="max-w-6xl mx-auto px-4 py-6 pb-12 -mt-4">
          {/* Search & Filters */}
          <Card className="mb-6 border-0 shadow-xl bg-white rounded-2xl overflow-hidden">
            <CardContent className="p-4 sm:p-6">
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      placeholder="Rechercher par nom, spécialité..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-12 h-12 sm:h-14 text-base rounded-xl border-slate-200 focus:border-[#1976D2] focus:ring-[#1976D2]"
                    />
                  </div>
                  <Button type="submit" className="h-12 sm:h-14 bg-[#1976D2] hover:bg-[#1565C0] rounded-xl px-6 font-medium">
                    <Search className="w-5 h-5 sm:mr-2" />
                    <span className="hidden sm:inline">Rechercher</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 sm:h-14 rounded-xl border-slate-200"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="w-5 h-5" />
                  </Button>
                </div>

                {/* Expandable Filters */}
                {showFilters && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
                    {/* City autocomplete */}
                    <div className="relative">
                      <label className="text-sm font-medium text-slate-700 mb-2 block">Ville</label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          ref={cityInputRef}
                          value={cityInput}
                          onChange={(e) => setCityInput(e.target.value)}
                          onFocus={() => cityInput && setShowCitySuggestions(true)}
                          placeholder="Tapez une ville..."
                          className="pl-10 pr-8 h-10"
                          data-testid="city-autocomplete-input"
                        />
                        {cityInput && (
                          <button
                            type="button"
                            onClick={clearCity}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* City suggestions dropdown */}
                      {showCitySuggestions && (citySuggestions.length > 0 || loadingCities) && (
                        <div 
                          ref={citySuggestionsRef}
                          className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto"
                        >
                          {loadingCities ? (
                            <div className="p-3 text-center text-slate-500 text-sm">
                              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                              Recherche...
                            </div>
                          ) : (
                            citySuggestions.map((city, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => selectCity(city)}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-100 flex items-center gap-2"
                              >
                                <MapPin className="w-3 h-3 text-slate-400" />
                                {city}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                      
                      {/* Show DB cities as quick options */}
                      {!cityInput && filters.cities.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {filters.cities.slice(0, 5).map((city) => (
                            <button
                              key={city}
                              type="button"
                              onClick={() => selectCity(city)}
                              className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600"
                            >
                              {city}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Specialty dropdown */}
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">Spécialité</label>
                      <select
                        value={selectedSpecialty}
                        onChange={(e) => { setSelectedSpecialty(e.target.value); setCurrentPage(1); }}
                        className="w-full h-10 px-3 border rounded-lg text-sm"
                        data-testid="specialty-select"
                      >
                        <option value="">Toutes les spécialités</option>
                        {filters.specialties.map((spec) => (
                          <option key={spec} value={spec}>{spec}</option>
                        ))}
                      </select>
                    </div>
                    
                    {(searchQuery || selectedCity || selectedSpecialty) && (
                      <div className="sm:col-span-2">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={clearFilters}
                          className="text-slate-500"
                        >
                          Effacer les filtres
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#1976D2]" />
            </div>
          ) : professionals.length === 0 ? (
            <Card className="border-0 shadow-xl rounded-2xl">
              <CardContent className="py-16 text-center">
                <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Stethoscope className="w-10 h-10 text-blue-300" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800">Aucun professionnel trouvé</h3>
                <p className="text-slate-500 text-sm mt-2 max-w-sm mx-auto">
                  Essayez de modifier vos critères de recherche ou d'élargir votre zone géographique.
                </p>
                <Button 
                  onClick={() => {
                    clearFilters();
                    loadProfessionals();
                  }} 
                  className="mt-6 bg-[#1976D2] hover:bg-[#1565C0] rounded-xl px-6"
                  data-testid="view-all-professionals-btn"
                >
                  Voir tous les professionnels
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Results count */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">{total}</span> résultat{total > 1 ? 's' : ''}
                </p>
              </div>
              
              {/* Professionals Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
                {professionals.map((pro) => (
                  <Card
                    key={pro.user_id}
                    className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden rounded-2xl bg-white hover:-translate-y-1"
                    onClick={() => navigate(`/pro/${pro.slug || pro.user_id}`)}
                    data-testid={`professional-card-${pro.user_id}`}
                  >
                    <CardContent className="p-0">
                      {/* Top gradient bar */}
                      <div className="h-2 bg-gradient-to-r from-[#1976D2] via-[#2196F3] to-[#42A5F5]" />
                      
                      <div className="p-5">
                        <div className="flex items-start gap-4">
                          {pro.profile_image ? (
                            <img
                              src={pro.profile_image}
                              alt={pro.full_name}
                              className="w-16 h-16 rounded-2xl object-cover ring-2 ring-white shadow-md"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl flex items-center justify-center shadow-md">
                              <User className="w-8 h-8 text-blue-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-slate-900 truncate text-base">
                                {pro.full_name?.startsWith('Dr.') || pro.full_name?.startsWith('Dr ') ? pro.full_name : `Dr. ${pro.full_name}`}
                              </h3>
                              {pro.is_verified && (
                                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-[#1976D2] font-semibold mt-0.5">
                              {pro.specialty || 'Médecin'}
                            </p>
                            {pro.city && (
                              <div className="flex items-center gap-1.5 mt-2 text-sm text-slate-500">
                                <MapPin className="w-3.5 h-3.5" />
                                <span>{pro.city}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Rating */}
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                          <div className="flex items-center gap-1.5">
                            <Star className={`w-4 h-4 ${pro.average_rating > 0 ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                            <span className="font-semibold text-sm text-slate-700">
                              {pro.average_rating > 0 ? pro.average_rating : '-'}
                            </span>
                            <span className="text-xs text-slate-400 ml-1">
                              ({pro.reviews_count || 0} avis)
                            </span>
                          </div>
                          <Button size="sm" className="bg-blue-50 text-[#1976D2] hover:bg-blue-100 border-0 rounded-lg text-xs font-medium">
                            <Calendar className="w-3 h-3 mr-1" />
                            Voir profil
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600 px-4 bg-slate-100 rounded-lg py-2">
                    Page {currentPage} sur {pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= pages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default ProfessionalsDirectory;
